"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.uiDocumentSchema = exports.UI_TEXT_EXTRA_PLACEHOLDERS = exports.UI_TEXT_PLACEHOLDERS = exports.QUALITY_REPORT_FIXED_TABS = exports.UI_SCREEN_COLUMN_IDS = exports.NAVIGATION_IDS = void 0;
exports.parseUiDocument = parseUiDocument;
// `config/ui.yaml` (SPEC §14.5, CR-001 §7): branding, navigation, screen texts and the columns of each
// screen. A column id is an entry in the registry below, which the web maps to a cell renderer; label,
// visibility, order and alignment come from the file. Texts carry `{placeholder}` keys filled from the
// scoring parameters of the selected year.
const zod_1 = require("zod");
const config_issue_js_1 = require("./config-issue.js");
const measures_js_1 = require("./measures.js");
/** SPEC §14.5 navigation ids, then CR-001 §7's; each screen's item adds its id to the default file. */
exports.NAVIGATION_IDS = [
    "groups",
    "aco_summary",
    "compare",
    "data_quality",
    "data",
    "settings",
    "practice",
    "practice_gaps",
    "closure_review",
    "closures",
    "aco_practices",
    "engine_reconciliation",
    "match_review",
    "shared_patients",
];
/**
 * Column ids per configurable screen: Screen 1 `groups` (CR-002 CR2-030: practice, ACO, and the measure
 * columns, one per measure in the measures document's order), Screen 2 `measures` and Screen 4
 * `patients` (deferred by CR-002, kept as they were), Screen 11 `practice_dashboard` and Screen 12's
 * two tables, `quality_report_consolidated` and `quality_report_measure` (CR-002 §6.8–§6.10).
 */
exports.UI_SCREEN_COLUMN_IDS = {
    groups: ["practice", "aco", "measures"],
    measures: [
        "measure",
        "total",
        "complete",
        "reporting_rate",
        "exceptions",
        "met",
        "not_met",
        "performance_rate",
        "excluded",
        "cms_completeness",
        "points",
    ],
    patients: [
        "name",
        "dob",
        "final_status",
        "strata",
        "tins",
        "npis",
        "last_encounter",
        "result",
        "instances",
        "multi_tin",
    ],
    practice_dashboard: [
        "measure",
        "numerator",
        "denominator",
        "performance",
        "potential_denominator",
    ],
    quality_report_consolidated: [
        "aco",
        "performance_group",
        "practice",
        "facility",
        "member_id",
        "first_name",
        "last_name",
        "dob",
        "inactive",
        "inactive_reason",
        "inactive_date",
        "clinician",
        "measure_status",
        "deceased",
    ],
    quality_report_measure: [
        "aco",
        "performance_group",
        "practice",
        "facility",
        "member_id",
        "first_name",
        "last_name",
        "dob",
        "inactive",
        "inactive_reason",
        "inactive_date",
        "clinician",
        "status",
        "qualifying_encounter",
        "qe_code",
        "qe_date",
        "bp",
        "exclusion_code",
        "exclusion_date",
        "numerator_code",
        "numerator_date",
        "action",
        "dx_code",
        "dx_date",
        "deceased",
        "death_date",
    ],
};
/** Screen 12's fixed tabs; the others are measure numbers (CR-002 CR2-060). */
exports.QUALITY_REPORT_FIXED_TABS = ["consolidated", "glossary"];
/**
 * Every `{placeholder}` a text may use, and the scoring parameter it reads. Each parameter is available
 * under its own name; `threshold` is SPEC §14.5's short name for the data completeness threshold.
 */
exports.UI_TEXT_PLACEHOLDERS = {
    ...Object.fromEntries(measures_js_1.scoringParametersSchema.keyof().options.map((name) => [name, name])),
    threshold: "data_completeness_threshold",
};
const COLUMN_ALIGNMENTS = ["left", "center", "right"];
const SCREEN_MODES = ["rate", "decile"];
function columnIdSchema(screen) {
    const ids = exports.UI_SCREEN_COLUMN_IDS[screen];
    return zod_1.z.enum(ids, {
        error: (issue) => `unknown column id ${String(issue.input)} for screen ${screen}; known ids: ${ids.join(", ")}`,
    });
}
function columnSchema(screen) {
    return zod_1.z.strictObject({
        id: columnIdSchema(screen),
        label: zod_1.z.string().min(1),
        visible: zod_1.z.boolean().default(true),
        align: zod_1.z.enum(COLUMN_ALIGNMENTS).default("left"),
    });
}
/**
 * Refuses an id listed twice on one screen. `lists` are the screen's column lists by key; `idKey` is
 * `id` where a column is an object and absent where it is a bare id (Screen 4).
 */
function checkUniqueColumns(screen, lists, idKey, ctx) {
    const seen = new Set();
    for (const [key, ids] of Object.entries(lists)) {
        ids.forEach((id, index) => {
            if (!seen.has(id))
                seen.add(id);
            else {
                const message = `column ${id} is already listed on screen ${screen}`;
                const at = screen.startsWith("quality_report_") ? "quality_report" : screen;
                ctx.addIssue({ code: "custom", path: [at, key, index, ...idKey], message });
            }
        });
    }
}
// J-072: the measure columns Screen 1 leaves out, by measure number; the others follow Screen 12's tabs.
const groupsScreenSchema = zod_1.z.strictObject({
    columns: zod_1.z.array(columnSchema("groups")).min(1),
    hidden_measures: zod_1.z
        .array(zod_1.z.string().regex(/^\d{3}$/, "measure number must have 3 digits"))
        .default([]),
});
const measuresScreenSchema = zod_1.z.strictObject({
    columns: zod_1.z.array(columnSchema("measures")).min(1),
    optional_columns: zod_1.z.array(columnSchema("measures")).default([]),
    show_strata: zod_1.z.boolean().default(true),
    show_group_name_under_title: zod_1.z.boolean().default(true),
    default_mode: zod_1.z.enum(SCREEN_MODES).default("rate"),
});
// SPEC §14.5 lists Screen 4's columns as bare ids: the labels are fixed by the gap list.
const patientsScreenSchema = zod_1.z.strictObject({
    columns: zod_1.z.array(columnIdSchema("patients")).min(1),
});
// Screen 11 (CR-002 CR2-050, §6.8): Potential Denominator stays hidden until an eligibility source exists.
const practiceDashboardScreenSchema = zod_1.z.strictObject({
    columns: zod_1.z.array(columnSchema("practice_dashboard")).min(1),
});
const qualityReportTabSchema = zod_1.z.union([zod_1.z.enum(exports.QUALITY_REPORT_FIXED_TABS), zod_1.z.string().regex(/^\d{3}$/)], {
    error: (issue) => `unknown tab ${String(issue.input)}: consolidated, glossary or a 3-digit measure number`,
});
/**
 * J-358 (the quality expert, 2026-10-27): Deceased and Death Date left Screen 12. They stay column ids, so a
 * stored ui document that still lists them parses everywhere, and the screen's lists drop them.
 */
const RETIRED_COLUMN_IDS = new Set(["deceased", "death_date"]);
const withoutRetired = (columns) => columns.filter((column) => !RETIRED_COLUMN_IDS.has(column.id));
// Screen 12 (CR-002 CR2-060, §6.9, §6.10): the tab order, then the Consolidated and measure-tab columns.
const qualityReportScreenSchema = zod_1.z
    .strictObject({
    tabs: zod_1.z.array(qualityReportTabSchema).min(1),
    consolidated_columns: zod_1.z
        .array(columnSchema("quality_report_consolidated"))
        .min(1)
        .transform(withoutRetired),
    measure_columns: zod_1.z
        .array(columnSchema("quality_report_measure"))
        .min(1)
        .transform(withoutRetired),
})
    .superRefine((screen, ctx) => {
    screen.tabs.forEach((tab, index) => {
        if (screen.tabs.indexOf(tab) !== index) {
            ctx.addIssue({
                code: "custom",
                path: ["tabs", index],
                message: `tab ${tab} is already listed`,
            });
        }
    });
});
const screensSchema = zod_1.z
    .strictObject({
    groups: groupsScreenSchema,
    measures: measuresScreenSchema,
    patients: patientsScreenSchema,
    practice_dashboard: practiceDashboardScreenSchema,
    quality_report: qualityReportScreenSchema,
})
    .superRefine((screens, ctx) => {
    const ids = (columns) => columns.map((column) => column.id);
    const { columns, optional_columns } = screens.measures;
    const measures = { columns: ids(columns), optional_columns: ids(optional_columns) };
    checkUniqueColumns("groups", { columns: ids(screens.groups.columns) }, ["id"], ctx);
    checkUniqueColumns("measures", measures, ["id"], ctx);
    checkUniqueColumns("patients", { columns: screens.patients.columns }, [], ctx);
    const dashboard = { columns: ids(screens.practice_dashboard.columns) };
    checkUniqueColumns("practice_dashboard", dashboard, ["id"], ctx);
    const report = screens.quality_report;
    checkUniqueColumns("quality_report_consolidated", { consolidated_columns: ids(report.consolidated_columns) }, ["id"], ctx);
    checkUniqueColumns("quality_report_measure", { measure_columns: ids(report.measure_columns) }, ["id"], ctx);
});
const hexColorSchema = zod_1.z.string().regex(/^#[0-9A-Fa-f]{6}$/, "colour must be #RRGGBB");
// SPEC §14.5: optional overrides applied only through the design system's theme mechanism.
const colorsSchema = zod_1.z.strictObject({
    primary: hexColorSchema.optional(),
    heading: hexColorSchema.optional(),
    background: hexColorSchema.optional(),
    card: hexColorSchema.optional(),
    bar_fill: hexColorSchema.optional(),
    bar_track: hexColorSchema.optional(),
    highlight: hexColorSchema.optional(),
    inverse_label: hexColorSchema.optional(),
    warning: hexColorSchema.optional(),
});
const brandingSchema = zod_1.z.strictObject({
    app_name: zod_1.z.string().min(1),
    logo: zod_1.z.string().min(1).nullable().default(null),
    registry_name: zod_1.z.string().min(1),
    colors: colorsSchema.nullable().default(null),
    font_family: zod_1.z.string().min(1).nullable().default(null),
});
const PLACEHOLDER = /\{([^{}]*)\}/g;
/**
 * The placeholders a CR-002 text may use besides the scoring parameters (CR2-023, CR2-031, CR2-036):
 * the ACO's name in the TOTAL row label, the two dates in the data line.
 */
exports.UI_TEXT_EXTRA_PLACEHOLDERS = {
    total_row_label: ["aco"],
    data_loaded: ["loaded", "calculated"],
};
const textSchema = zod_1.z
    .strictObject({
    patient_matching_note: zod_1.z.string(),
    footnote: zod_1.z.string(),
    completeness_tooltip: zod_1.z.string(),
    inverse_label: zod_1.z.string(),
    stratum_prefix: zod_1.z.string(),
    stratum_suffix: zod_1.z.string(),
    // CR-002 §8: the gap-inclusive label (CR2-158), Screen 6's scored-rate label (CR2-024), the TOTAL
    // row (CR2-031), the data line (CR2-036) and #1's note (CR2-032).
    performance_label: zod_1.z.string().min(1),
    scored_rate_label: zod_1.z.string().min(1),
    total_row_label: zod_1.z.string().min(1),
    data_loaded: zod_1.z.string().min(1),
    lower_is_better: zod_1.z.string().min(1),
    // J-274 (NR-05; CR2-064, wording is configuration): the Action to Take of an inactive patient's open
    // gap, and the Inactive reason's two words. A ui document stored before J-274 lacks them, so each
    // defaults to the shipped wording until `release:tidy` writes it into the stored document.
    inactive_action: zod_1.z.string().min(1).default("Chart review: patient inactive"),
    inactive_deceased: zod_1.z.string().min(1).default("Deceased"),
    inactive_discharged: zod_1.z.string().min(1).default("Discharged"),
})
    .superRefine((text, ctx) => {
    for (const [key, value] of Object.entries(text)) {
        const extra = exports.UI_TEXT_EXTRA_PLACEHOLDERS[key] ?? [];
        for (const [placeholder, name = ""] of value.matchAll(PLACEHOLDER)) {
            if (Object.hasOwn(exports.UI_TEXT_PLACEHOLDERS, name) || extra.includes(name))
                continue;
            const known = [...Object.keys(exports.UI_TEXT_PLACEHOLDERS), ...extra].join(", ");
            const message = `placeholder ${placeholder} is not a scoring parameter; known: ${known}`;
            ctx.addIssue({ code: "custom", path: [key], message });
        }
    }
});
// CR-002 N-08, CR2-066: the Glossary tab's terms, in order.
const glossarySchema = zod_1.z
    .array(zod_1.z.strictObject({ term: zod_1.z.string().min(1), definition: zod_1.z.string().min(1) }))
    .min(1);
const navigationSchema = zod_1.z
    .array(zod_1.z.enum(exports.NAVIGATION_IDS))
    .min(1)
    .superRefine((items, ctx) => {
    items.forEach((item, index) => {
        if (items.indexOf(item) !== index) {
            const message = `navigation item ${item} is already listed`;
            ctx.addIssue({ code: "custom", path: [index], message });
        }
    });
});
exports.uiDocumentSchema = zod_1.z.strictObject({
    branding: brandingSchema,
    navigation: navigationSchema,
    text: textSchema,
    screens: screensSchema,
    glossary: glossarySchema,
});
/** Parses `config/ui.yaml`; issues name the document `ui`. */
function parseUiDocument(input) {
    return (0, config_issue_js_1.parseConfigDocument)(exports.uiDocumentSchema, "ui", input);
}
//# sourceMappingURL=ui.js.map